import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import type {ClassInfo} from 'lit/directives/class-map.js';
import {classMap} from 'lit/directives/class-map.js';
import {ifDefined} from 'lit/directives/if-defined.js';
import {FlywheelComponent} from '@gl/lit/lib/component.js';
import iconstyle from './style.scss';
import '@material/mwc-icon';

export const COMPONENT_TAG = 'fly-icon';

export enum Size {
  BASE = 'base',
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
  XL2 = '2xl',
  XL3 = '3xl',
  XL4 = '4xl',
  XL5 = '5xl',
}

export enum Fill {
  BASE = 'base',
  PRIMARY = 'primary',
  PRIMARY_DARK = 'primary--dark',
  SECONDARY = 'secondary',
  SECONDARY_DARK = 'secondary--dark',
  SUCCESS = 'green',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  GREEN = 'green',
  SUCCESS_DARK = 'green--dark',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  GREEN_DARK = 'green--dark',
  WARNING = 'yellow',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  YELLOW = 'yellow',
  WARNING_DARK = 'yellow--dark',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  YELLOW_DARK = 'yellow--dark',
  DANGER = 'red',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  RED = 'red',
  DANGER_DARK = 'red--dark',
  // eslint-disable-next-line @typescript-eslint/no-duplicate-enum-values
  RED_DARK = 'red--dark',
  WHITE = 'white',
  WHITE_DARK = 'white--dark',
}

const SVGIcons = new Map<string, string>();

export const DEFAULT_SVG_ICON_SIZE = 'var(--fly-icon-size, auto)';

// Most of the icons we currently use, added for loose autocomplete,
// you can type <fly-icon icon="" and get autocomplete in between
// the empty string for the icon parameter
export type MaterialIcons =
  | 'expand_more'
  | 'expand_less'
  | 'online_prediction'
  | 'grading'
  | 'route'
  | 'content_copy'
  | 'query_stats'
  | 'storage'
  | 'schema'
  | 'sync_alt'
  | 'corporate_fare'
  | 'settings'
  | 'close'
  | 'error'
  | 'search'
  | 'person'
  | 'check_circle'
  | 'delete'
  | 'more_vert'
  | 'dashboard'
  | 'people'
  | 'more_horiz'
  | 'add'
  | 'chevron_left'
  | 'arrow_backwards'
  | 'arrow_forwards'
  | 'warning_amber'
  | 'refresh'
  | 'menu_book'
  | 'auto_awesome'
  | 'remove_circle_outline'
  | 'restart_alt'
  | 'keyboard_arrow_right'
  | 'keyboard_arrow_left'
  | 'remove'
  | 'swap_horiz'
  | 'login'
  | 'logout'
  | (string & {});

/**
 * **FlyIcon**
 *
 * To find the available icons, access [MUI icons](https://fonts.google.com/icons?icon.platform=web&icon.set=Material+Icons)
 *
 * Be aware that we may not support some of it.
 */
@customElement(COMPONENT_TAG)
export default class FlyIcon extends FlywheelComponent {
  @property()
  size: Size = Size.BASE;

  @property()
  fill: Fill = Fill.BASE;

  @property()
  icon = '' as MaterialIcons;

  @property({type: Boolean})
  svg = false;

  @property({type: String})
  alt?: string;

  @property({type: String})
  svgIconSize = DEFAULT_SVG_ICON_SIZE;

  get classes(): ClassInfo {
    return {
      'fly-icon': true,
      'fly-icon--svg': this.svg,
      [`fly-icon--size--${this.size}`]: true,
      [`fly-icon--${this.fill}`]: true,
      [`fly-icon--svg--${this.fill}`]: true,
    };
  }

  static get styles() {
    return [iconstyle];
  }

  render() {
    if (this.svg) {
      return html`
        <div class=${classMap(this.classes)}>
          <img
            alt=${ifDefined(this.alt)}
            src=${SVGIcons.get(this.icon) || this.icon}
            width=${this.svgIconSize}
            height=${this.svgIconSize}
          />
        </div>
      `;
    }

    return html`
      <mwc-icon class=${classMap(this.classes)}> ${this.icon} </mwc-icon>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [COMPONENT_TAG]: FlyIcon;
  }
}
